import React, { useState, useEffect } from 'react'
import { 
  Calendar, Download, Settings, Share2, 
  Link, Copy, Check, RefreshCw, Globe,
  FileText, Mail, Users, Plus, Trash2, X
} from 'lucide-react'
import { apiClient } from '@/utils/api'
import toast from 'react-hot-toast'
import { format, startOfMonth, endOfMonth, addMonths } from 'date-fns'

interface ExportConfig {
  format: 'ical' | 'google' | 'csv' | 'json'
  dateRange: 'month' | 'quarter' | 'year' | 'custom'
  includeChores: boolean
  includeEvents: boolean
  includeRewards: boolean
  includeAssignments: boolean
  customStartDate?: string
  customEndDate?: string
  selectedKids: string[]
}

interface CalendarLink {
  id: string
  name: string
  url: string
  token: string
  user_id?: string
  is_active: boolean
  created_at: string
  last_accessed?: string
  access_count: number
}

interface Kid {
  id: string
  username: string
  display_name: string
}

export const CalendarExport: React.FC = () => {
  const [exportConfig, setExportConfig] = useState<ExportConfig>({
    format: 'ical',
    dateRange: 'month',
    includeChores: true,
    includeEvents: true,
    includeRewards: false,
    includeAssignments: true,
    selectedKids: []
  })
  
  const [calendarLinks, setCalendarLinks] = useState<CalendarLink[]>([])
  const [kids, setKids] = useState<Kid[]>([])
  const [loading, setLoading] = useState(false)
  const [showCreateLink, setShowCreateLink] = useState(false)
  const [copiedLink, setCopiedLink] = useState<string | null>(null)
  
  const [newLink, setNewLink] = useState({
    name: '',
    user_id: '',
    includeAll: true
  })

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const [linksData, usersData] = await Promise.all([
        apiClient.get('/api/calendar/links'),
        apiClient.get('/api/users')
      ])
      setCalendarLinks(linksData)
      setKids(usersData.filter((u: any) => !u.is_parent && u.is_active))
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      const params = new URLSearchParams()
      params.append('format', exportConfig.format)
      params.append('date_range', exportConfig.dateRange)
      params.append('include_chores', exportConfig.includeChores.toString())
      params.append('include_events', exportConfig.includeEvents.toString())
      params.append('include_rewards', exportConfig.includeRewards.toString())
      params.append('include_assignments', exportConfig.includeAssignments.toString())
      
      if (exportConfig.dateRange === 'custom') {
        params.append('start_date', exportConfig.customStartDate || '')
        params.append('end_date', exportConfig.customEndDate || '')
      }
      
      if (exportConfig.selectedKids.length > 0) {
        params.append('kid_ids', exportConfig.selectedKids.join(','))
      }

      const response = await apiClient.get(`/api/calendar/export?${params.toString()}`, {
        responseType: 'blob'
      })
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response]))
      const link = document.createElement('a')
      link.href = url
      
      const filename = `chores-calendar-${format(new Date(), 'yyyy-MM-dd')}.${
        exportConfig.format === 'ical' ? 'ics' : exportConfig.format
      }`
      link.setAttribute('download', filename)
      
      document.body.appendChild(link)
      link.click()
      link.remove()
      
      toast.success('Calendar exported successfully')
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to export calendar')
    }
  }

  const handleCreateLink = async () => {
    if (!newLink.name) {
      toast.error('Please provide a name for the calendar link')
      return
    }

    try {
      const response = await apiClient.post('/api/calendar/links', {
        name: newLink.name,
        user_id: newLink.includeAll ? null : newLink.user_id
      })
      
      toast.success('Calendar link created successfully')
      setShowCreateLink(false)
      setNewLink({ name: '', user_id: '', includeAll: true })
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to create calendar link')
    }
  }

  const handleDeleteLink = async (linkId: string) => {
    if (!confirm('Are you sure you want to delete this calendar link?')) return
    
    try {
      await apiClient.delete(`/api/calendar/links/${linkId}`)
      toast.success('Calendar link deleted')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete link')
    }
  }

  const handleToggleLink = async (linkId: string, isActive: boolean) => {
    try {
      await apiClient.patch(`/api/calendar/links/${linkId}`, { is_active: !isActive })
      toast.success(`Link ${!isActive ? 'activated' : 'deactivated'}`)
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to update link')
    }
  }

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url)
    setCopiedLink(url)
    toast.success('Link copied to clipboard')
    setTimeout(() => setCopiedLink(null), 3000)
  }

  const handleRegenerateLink = async (linkId: string) => {
    if (!confirm('This will invalidate the current link. Continue?')) return
    
    try {
      await apiClient.post(`/api/calendar/links/${linkId}/regenerate`)
      toast.success('Link regenerated successfully')
      loadData()
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to regenerate link')
    }
  }

  const getDateRangeText = () => {
    const today = new Date()
    switch (exportConfig.dateRange) {
      case 'month':
        return `${format(startOfMonth(today), 'MMM d')} - ${format(endOfMonth(today), 'MMM d, yyyy')}`
      case 'quarter':
        return `${format(today, 'QQQ yyyy')}`
      case 'year':
        return format(today, 'yyyy')
      case 'custom':
        return exportConfig.customStartDate && exportConfig.customEndDate
          ? `${exportConfig.customStartDate} to ${exportConfig.customEndDate}`
          : 'Select dates'
      default:
        return ''
    }
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Calendar Export & Sync</h2>
        <p className="text-gray-600">Export calendar data and manage subscription links</p>
      </div>

      {/* Export Section */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4">Export Calendar Data</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-2">Export Format</label>
            <select
              value={exportConfig.format}
              onChange={(e) => setExportConfig({ ...exportConfig, format: e.target.value as any })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="ical">iCal (.ics)</option>
              <option value="google">Google Calendar</option>
              <option value="csv">CSV Spreadsheet</option>
              <option value="json">JSON Data</option>
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-2">Date Range</label>
            <select
              value={exportConfig.dateRange}
              onChange={(e) => setExportConfig({ ...exportConfig, dateRange: e.target.value as any })}
              className="w-full px-3 py-2 border rounded-lg"
            >
              <option value="month">This Month</option>
              <option value="quarter">This Quarter</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
        </div>

        {exportConfig.dateRange === 'custom' && (
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium mb-2">Start Date</label>
              <input
                type="date"
                value={exportConfig.customStartDate || ''}
                onChange={(e) => setExportConfig({ ...exportConfig, customStartDate: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">End Date</label>
              <input
                type="date"
                value={exportConfig.customEndDate || ''}
                onChange={(e) => setExportConfig({ ...exportConfig, customEndDate: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Include Data</label>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportConfig.includeChores}
                onChange={(e) => setExportConfig({ ...exportConfig, includeChores: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Chore Schedules</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportConfig.includeEvents}
                onChange={(e) => setExportConfig({ ...exportConfig, includeEvents: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Kid Events & Exceptions</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportConfig.includeAssignments}
                onChange={(e) => setExportConfig({ ...exportConfig, includeAssignments: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Chore Assignments</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={exportConfig.includeRewards}
                onChange={(e) => setExportConfig({ ...exportConfig, includeRewards: e.target.checked })}
                className="mr-2"
              />
              <span className="text-sm">Reward Redemptions</span>
            </label>
          </div>
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">Filter by Kids</label>
          <div className="border rounded-lg p-3 max-h-32 overflow-y-auto">
            {kids.map(kid => (
              <label key={kid.id} className="flex items-center mb-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={exportConfig.selectedKids.includes(kid.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setExportConfig({
                        ...exportConfig,
                        selectedKids: [...exportConfig.selectedKids, kid.id]
                      })
                    } else {
                      setExportConfig({
                        ...exportConfig,
                        selectedKids: exportConfig.selectedKids.filter(id => id !== kid.id)
                      })
                    }
                  }}
                  className="mr-2"
                />
                <span className="text-sm">{kid.display_name}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Export range: {getDateRangeText()}
          </div>
          <button
            onClick={handleExport}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Calendar
          </button>
        </div>
      </div>

      {/* Calendar Links Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Calendar Subscription Links</h3>
          <button
            onClick={() => setShowCreateLink(true)}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Link
          </button>
        </div>

        <div className="space-y-3">
          {calendarLinks.map(link => {
            const kid = kids.find(k => k.id === link.user_id)
            return (
              <div key={link.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <h4 className="font-semibold text-gray-900">{link.name}</h4>
                      <span className={`ml-2 px-2 py-1 text-xs rounded ${
                        link.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {link.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mb-2">
                      {kid ? `For: ${kid.display_name}` : 'All family members'}
                    </div>
                    <div className="flex items-center space-x-4 text-xs text-gray-500">
                      <span>Created: {format(new Date(link.created_at), 'MMM d, yyyy')}</span>
                      <span>Accessed: {link.access_count} times</span>
                      {link.last_accessed && (
                        <span>Last: {format(new Date(link.last_accessed), 'MMM d')}</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleCopyLink(link.url)}
                      className="p-2 text-blue-600 hover:text-blue-900"
                      title="Copy link"
                    >
                      {copiedLink === link.url ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleRegenerateLink(link.id)}
                      className="p-2 text-yellow-600 hover:text-yellow-900"
                      title="Regenerate link"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleToggleLink(link.id, link.is_active)}
                      className="p-2 text-gray-600 hover:text-gray-900"
                      title={link.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {link.is_active ? <X className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDeleteLink(link.id)}
                      className="p-2 text-red-600 hover:text-red-900"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="mt-3 p-2 bg-gray-50 rounded text-xs font-mono text-gray-600 break-all">
                  {link.url}
                </div>
              </div>
            )
          })}
        </div>

        {calendarLinks.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No calendar links created yet</p>
            <p className="text-sm mt-1">Create a link to sync with external calendars</p>
          </div>
        )}
      </div>

      {/* Create Link Modal */}
      {showCreateLink && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Create Calendar Link</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Link Name *</label>
                <input
                  type="text"
                  value={newLink.name}
                  onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="e.g., Family Calendar, John's Schedule"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Include Events For</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={newLink.includeAll}
                      onChange={() => setNewLink({ ...newLink, includeAll: true, user_id: '' })}
                      className="mr-2"
                    />
                    <span className="text-sm">All family members</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      checked={!newLink.includeAll}
                      onChange={() => setNewLink({ ...newLink, includeAll: false })}
                      className="mr-2"
                    />
                    <span className="text-sm">Specific child only</span>
                  </label>
                </div>
                
                {!newLink.includeAll && (
                  <select
                    value={newLink.user_id}
                    onChange={(e) => setNewLink({ ...newLink, user_id: e.target.value })}
                    className="w-full mt-2 px-3 py-2 border rounded-lg"
                  >
                    <option value="">Select a child...</option>
                    {kids.map(kid => (
                      <option key={kid.id} value={kid.id}>{kid.display_name}</option>
                    ))}
                  </select>
                )}
              </div>

              <div className="p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  This will create a unique URL that can be added to any calendar app 
                  (Google Calendar, Apple Calendar, Outlook, etc.) to automatically sync 
                  chore schedules and events.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateLink(false)
                  setNewLink({ name: '', user_id: '', includeAll: true })
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateLink}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                Create Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}